import { z } from "zod";

import { MetadataSchema } from "api/schemas/common";
import { DurationIconSchema, DurationUnitSchema } from "api/services/content/schemas/shared";

export const CalculatedDurationWeeksSchema = z.number();
export type CalculatedDurationWeeks = z.infer<typeof CalculatedDurationWeeksSchema>;

export const DurationWeeksSchema = z.number().nullable();
export type DurationWeeks = z.infer<typeof DurationWeeksSchema>;

const DaysSchema = z.array(z.number().min(1).max(7));

const ExercisesPerDaySchema = z.number().min(1).max(12);
export type ExercisesPerDay = z.infer<typeof ExercisesPerDaySchema>;

export const HealthFormTypeSchema = z.enum([
  "fall_prevention",
  "generic",
  "hand_oa",
  "hip_oa",
  "knee_oa",
  "low_back_pain",
  "neck",
  "osteoporosis",
  "shoulder_pain",
]);
export type HealthFormType = z.infer<typeof HealthFormTypeSchema>;

export const GroupSchema = z.enum([
  "back",
  "elbow",
  "fall_prevention",
  "foot",
  "hand",
  "hip",
  "knee",
  "lower_leg",
  "neck",
  "osteoporosis",
  "shoulder",
]);
export type Group = z.infer<typeof GroupSchema>;

export const KeySchema = z.enum([
  "achilles_tendinopathy",
  "ankle_instability",
  "fall_prevention",
  "foot_oa_or_unspecified",
  "general_elbow_pain",
  "hallux_valgus",
  "hand_oa",
  "hip_oa",
  "hip_pain_unspecific",
  "knee_instability",
  "knee_oa",
  "lateral_epicondylalgia",
  "low_back_pain",
  "medial_epicondylalgia",
  "neck",
  "osteoporosis",
  "patellofemoral_pain_syndrome",
  "plantar_faschiit",
  "post_op_hip",
  "post_op_knee",
  "pre_op_hip",
  "pre_op_knee",
  "shoulder_pain",
  "trochanterit",
  "wrist_pain",
]);
export type Key = z.infer<typeof KeySchema>;

const TimestampFieldsSchema = z.object({
  created_at: z.string(),
  updated_at: z.string(),
});

const BaseTreatmentScheduleFieldsSchema = z.object({
  duration_weeks: DurationWeeksSchema,
  exercises_per_day: ExercisesPerDaySchema,
  health_form_type: HealthFormTypeSchema,
  includes_functional_test: z.boolean(),
});

// ============================================================================
// Exercise Schemas
// ============================================================================

const BaseExerciseSchema = z.object({
  days: DaysSchema.nullable(),
  duration_icon: DurationIconSchema,
  duration_unit: DurationUnitSchema,
  exercise_id: z.number(),
  level_locked: z.boolean(),
  pinned: z.boolean(),
  recommended_level: z.number(),
  recommended_reps: z.string().nullable(),
  recommended_sets: z.number().min(1).nullable(),
  reps: z.string(),
  sets: z.number().min(1),
  weeks: z.array(z.number()),
});

const TreatmentScheduleExercisePayloadSchema = z.object({
  ...BaseExerciseSchema.shape,
  duration_icon: DurationIconSchema.nullable(),
  duration_unit: DurationUnitSchema.nullable(),
  recommended_reps: z.string().nullable().optional(),
  recommended_sets: z.number().min(1).nullable().optional(),
  reps: z.string().optional(),
  sets: z.number().min(1).optional(),
});
export type TreatmentScheduleExercisePayload = z.infer<typeof TreatmentScheduleExercisePayloadSchema>;

const PatientTreatmentScheduleExerciseSchema = z.object({
  ...BaseExerciseSchema.shape,
  image_url: z.string().nullable(),
  max_level: z.number(),
  title: z.string(),
  variation_title: z.string().nullable(),
  video_thumbnail_file_url: z.string().nullable(),
});
export type PatientTreatmentScheduleExercise = z.infer<typeof PatientTreatmentScheduleExerciseSchema>;

const TreatmentScheduleTemplateExerciseSchema = z.object({
  ...PatientTreatmentScheduleExerciseSchema.shape,
  ...TimestampFieldsSchema.shape,
  id: z.number(),
  treatment_schedule_id: z.number(),
});

// ============================================================================
// Lesson Schemas
// ============================================================================

const BaseLessonSchema = z.object({
  position: z.number(),
  text_lesson_id: z.number(),
});

const PatientTreatmentScheduleLessonSchema = z.object({
  ...BaseLessonSchema.shape,
  image_url: z.string().nullable(),
  title: z.string(),
});

const TreatmentScheduleTemplateLessonSchema = z.object({
  ...PatientTreatmentScheduleLessonSchema.shape,
  ...TimestampFieldsSchema.shape,
  id: z.number(),
  treatment_schedule_id: z.number(),
});

// ============================================================================
// Treatment Schedule Payload Schemas (for POST/PUT)
// ============================================================================

export const ConvertTreatmentTemplatePayloadSchema = z.object({
  duration_weeks: DurationWeeksSchema,
  group: GroupSchema,
  template_id: z.number(),
});
export type ConvertTreatmentTemplatePayload = z.infer<typeof ConvertTreatmentTemplatePayloadSchema>;

export const TreatmentSchedulePayloadSchema = z.object({
  ...BaseTreatmentScheduleFieldsSchema.shape,
  calculated_duration_weeks: CalculatedDurationWeeksSchema.optional(),
  exercises: z.array(TreatmentScheduleExercisePayloadSchema).min(1),
  group: GroupSchema.nullable(),
  key: KeySchema.nullable(),
  lessons: z.array(BaseLessonSchema).optional(),
  name: z.string().nullable(),
});
export type PostTreatmentSchedulePayload = z.infer<typeof TreatmentSchedulePayloadSchema>;

export const LibraryTreatmentSchedulePayloadSchema = TreatmentSchedulePayloadSchema.extend({
  group: GroupSchema,
  name: z.string(),
});
export type PostLibraryTreatmentSchedulePayload = z.infer<typeof LibraryTreatmentSchedulePayloadSchema>;

const PutTreatmentScheduleArgsSchema = z.object({
  treatmentScheduleID: z.number(),
  payload: TreatmentSchedulePayloadSchema,
});
export type PutTreatmentScheduleArgs = z.infer<typeof PutTreatmentScheduleArgsSchema>;

const PutPatientTreatmentScheduleSchema = z.object({
  patientID: z.number(),
  payload: TreatmentSchedulePayloadSchema,
});
export type PutPatientTreatmentScheduleArgs = z.infer<typeof PutPatientTreatmentScheduleSchema>;

// ============================================================================
// Treatment Schedule Response Schemas
// ============================================================================

export const ConvertTreatmentTemplateResponseSchema = z.object({
  ID: z.number(),
});
export type ConvertTreatmentTemplateResponse = z.infer<typeof ConvertTreatmentTemplateResponseSchema>;

export const TreatmentSchedulesItemSchema = z.object({
  ...BaseTreatmentScheduleFieldsSchema.shape,
  ...TimestampFieldsSchema.shape,
  creator_id: z.number().nullable(),
  group: GroupSchema,
  id: z.number(),
  key: KeySchema.nullable(),
  name: z.string(),
});
export type TreatmentSchedulesItem = z.infer<typeof TreatmentSchedulesItemSchema>;

export const TreatmentSchedulesResponseSchema = z.object({
  items: z.array(TreatmentSchedulesItemSchema),
  metadata: MetadataSchema,
});
export type TreatmentSchedulesResponse = z.infer<typeof TreatmentSchedulesResponseSchema>;

export const TreatmentScheduleTemplateSchema = z.object({
  ...TreatmentSchedulesItemSchema.shape,
  exercises: z.array(TreatmentScheduleTemplateExerciseSchema),
  lessons: z.array(TreatmentScheduleTemplateLessonSchema),
});
export type TreatmentScheduleTemplate = z.infer<typeof TreatmentScheduleTemplateSchema>;

// ============================================================================
// Patient Treatment Schedule Schemas
// ============================================================================

export const PatientTreatmentScheduleSchema = z.object({
  ...BaseTreatmentScheduleFieldsSchema.shape,
  calculated_duration_weeks: CalculatedDurationWeeksSchema,
  exercises: z.array(PatientTreatmentScheduleExerciseSchema),
  lessons: z.array(PatientTreatmentScheduleLessonSchema),
  version: z.number(),
});
export type PatientTreatmentSchedule = z.infer<typeof PatientTreatmentScheduleSchema>;

// ============================================================================
// Patient Treatment Schedule Activity Schemas
// ============================================================================

const UpdatePatientTreatmentScheduleActivityArgsSchema = z.object({
  patientID: z.number(),
  userActivityScheduleID: z.number(),
});
export type UpdatePatientTreatmentScheduleActivityArgs = z.infer<
  typeof UpdatePatientTreatmentScheduleActivityArgsSchema
>;

const PutPatientTreatmentScheduleActivityArgsSchema = z.object({
  patientID: z.number(),
  userActivityScheduleID: z.number(),
  payload: z.object({ day_number: z.number().min(1).max(7), week_number: z.number().min(1) }),
});
export type PutPatientTreatmentScheduleActivityArgs = z.infer<typeof PutPatientTreatmentScheduleActivityArgsSchema>;

// ============================================================================
// Treatment Schedule Groups Schemas
// ============================================================================

const TreatmentScheduleGroupsSchema = z.object({
  id: z.number(),
  key: GroupSchema,
});

export const TreatmentScheduleGroupsResponseSchema = z.object({
  groups: z.array(TreatmentScheduleGroupsSchema),
});
export type TreatmentScheduleGroupsResponse = z.infer<typeof TreatmentScheduleGroupsResponseSchema>;
